import { getLocalisedName } from "./i18n";

export interface AdminAreaChainItem {
  id: number;
  name: string;
  name_translations?: Record<string, string>;
  type: string;
  type_translations?: Record<string, string>;
  admin_level: number;
}

export const formatAdminAreaChain = (chain?: AdminAreaChainItem[] | null): string =>
  (chain ?? [])
    .map(area => getLocalisedName(area))
    .filter(Boolean)
    .join(", ");

// Aggregator option label: the name followed by its admin area chain, e.g.
// "Coop A (Region, District, Ward)". The aggregator pickers search on the label, so
// the chain also makes aggregators searchable by admin area. Falls back to the bare
// name when the chain is missing (list fetched without `with_location`).
export const formatAggregatorLabel = (aggregator: {
  name: string;
  admin_area?: AdminAreaChainItem[] | null;
}): string => {
  const chain = formatAdminAreaChain(aggregator.admin_area);
  return chain ? `${aggregator.name} (${chain})` : aggregator.name;
};

export interface AdminAreaNode {
  id: number | string;
  name: string;
  name_translations?: Record<string, string>;
  parent: number | string | null;
}

export interface AdminAreaTreeOption {
  value: number | string;
  label: string;
  depth: number;
}

const ROOT_KEY = "root";

export const buildAdminAreaTreeOptions = (areas: AdminAreaNode[]): AdminAreaTreeOption[] => {
  const areaById = new Map(areas.map(area => [area.id, area]));
  const localName = (area: AdminAreaNode): string =>
    (getLocalisedName(area) as string | undefined) ?? area.name;

  const childrenByParent = new Map<number | string, AdminAreaNode[]>();
  areas.forEach(area => {
    const parentKey = area.parent != null && areaById.has(area.parent) ? area.parent : ROOT_KEY;
    const siblings = childrenByParent.get(parentKey) ?? [];
    siblings.push(area);
    childrenByParent.set(parentKey, siblings);
  });
  childrenByParent.forEach(siblings =>
    siblings.sort((first, second) => localName(first).localeCompare(localName(second)))
  );

  const options: AdminAreaTreeOption[] = [];
  const visited = new Set<number | string>();
  const walk = (parentKey: number | string, depth: number) => {
    (childrenByParent.get(parentKey) ?? []).forEach(area => {
      if (visited.has(area.id)) {
        return;
      }
      visited.add(area.id);
      options.push({ value: area.id, label: localName(area), depth });
      walk(area.id, depth + 1);
    });
  };
  walk(ROOT_KEY, 0);

  return options;
};
